<template>
  <aside-container class="participant-detail">
    <div class="view-spacing">
      <template
        v-if="participant"
      >
        <ParticipantHero2023
          :participant="participant"
        />
        <race-progress
          v-if="mode === $options.modes.LIVE && participant && participantHasDistance"
          :runners="[participant]"
          :mark="participant.personalGoal"
          :show-no1-badge="false"
          class="my-3"
        >
          <template #header="{ runners }">
            <HighlightedText
              class="card badge p-2 text-uppercase"
              :text="$t('participant.raceProgress', [runners[0].firstname])"
            />
          </template>
        </race-progress>
        <FundraisingWidget :participant="participant"/>

        <AudioStickerParticipantCard
          v-if="showAudioStickerCard && hasFeature('fanCheering')"
          :participant="participant"
        />

        <RunnerFacts :participant="participant"/>
      </template>
      <Loading
        v-else
        key="loading"
        class="my-4"
      />

      <div
        v-show="showMap && !media.lg"
        slot="full"
        class="track-map-overlay"
      >
        <transition name="fade">
          <keep-alive>
            <track-map
              v-if="location && showMap && !media.lg"
              :id="location.id"
            />
          </keep-alive>
        </transition>
      </div>
    </div>
  </aside-container>
</template>

<script>
import Loading from '@/components/Loading.vue'
import AudioStickerParticipantCard from '@/components/AudioStickerParticipantCard.vue'
import RaceProgress from '@/components/location/RaceProgress.vue'
import ParticipantHero2023 from '@/components/detail/ParticipantHero2023.vue'
import HighlightedText from '@/components/HighlightedText.vue'
import RunnerFacts from '@/components/profile/RunnerFacts.vue'
import FundraisingWidget from '@/components/profile/FundraisingWidget.vue'
import { mapGetters, mapActions } from 'vuex'
import getIoc from '@/util/ioc'
import { athleteTypes, status } from '@/constants/participant'
import countries from '@/mixins/countries'
import time from '@/filters/time'
import * as modes from '@/constants/modes'

const TrackMap = () => import(/* webpackChunkName: "TrackMap" */ '@/views/main/TrackMap.vue')

export default {
  name: 'ParticipantDetail',
  time,
  athleteTypes,
  modes,
  status,
  components: {
    FundraisingWidget,
    RunnerFacts,
    AudioStickerParticipantCard,
    ParticipantHero2023,
    RaceProgress,
    TrackMap,
    HighlightedText,
    Loading
  },
  mixins: [countries],
  inject: ['subscribeParticipant', 'media', 'updateMetaTags'],
  props: {
    id: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      showMap: false
    }
  },
  computed: {
    ...mapGetters([
      'mode',
      'locationById',
      'participantById',
      'unitDistance',
      'catcherCarDistance',
      'hasFeature'
    ]),
    participant() {
      const participant = this.participantById(this.id)
      if (!participant || (participant && participant.incomplete)) {
        this.getParticipantById(this.id)
      }

      if (!participant) {
        return undefined
      }

      return participant
    },
    isAppRunner() {
      if (!this.participant) {
        return false
      }

      return this.participant.athleteType === athleteTypes.APP
    },
    location() {
      const { participant } = this
      if (participant) {
        return this.locationById(participant.locationId)
      }

      return null
    },
    showAudioStickerCard() {
      return this.isAppRunner
        && ![status.FINISHED, status.CAUGHT, status.OFFLINE, status.CANCELLED].includes(this.participant.state)
    },
    participantHasDistance() {
      return this.participant.distance > 0
    }
  },
  watch: {
    id(id, oldId) {
      if (id !== oldId && this.$options.unSubscripe) {
        this.$options.unSubscripe.then((cb) => {
          cb()
          this.$options.unSubscripe = this.subscribeParticipant(id)
        })
      }
    },
    // eslint-disable-next-line
    'participant.userId': function () {
      this.donateFromSearch()
    },
    // eslint-disable-next-line
    'participant.fullname': function () {
      this.updateMetaTags({ title: this.participant.fullname })
    },
    // eslint-disable-next-line
    '$route.query.donateDistance': function () {
      this.donateFromSearch()
    }
  },
  mounted() {
    this.$options.unSubscripe = this.subscribeParticipant(this.id)
    this.donateFromSearch()
    this.updateMetaTags({ title: this.participant?.fullname })
  },
  beforeRemove() {
    this.$options.unSubscripe.then(cb => cb())
    this.$options.unSubscripe = null
  },
  activated() {
    if (!this.$options.unSubscripe) {
      this.$options.unSubscripe = this.subscribeParticipant(this.id)
    }
  },
  deactivated() {
    this.$options.unSubscripe.then(cb => cb())
    this.$options.unSubscripe = null
  },
  methods: {
    ...mapActions([
      'getParticipantById',
      'donate'
    ]),
    getIoc,
    donateFromSearch() {
      if (this.id !== this.participant?.id || !this.$route.query.donateDistance) {
        return
      }

      if (this.hasFeature('boostRunner') && this.mode !== modes.POST) {
        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            donateDistance: undefined
          }
        })
        this.donate({
          participantId: this.participant.id,
          distance: true
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "~@/scss/bootstrap/custom";
</style>
